import { FC } from 'react';
import { Modal, Typography, Button } from '@crisellevaldez/speculo-ui';
import { Check, Download, FileText } from 'lucide-react';
import { formatDate } from '../../../utils/dateTime';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';

// Define the response type from the API
export interface UploadResponse {
  success: boolean;
  processed: number;
  skipped: number;
  total: number;
  opt_in: boolean;
  upload_tag: string;
  message: string;
  errors?: Array<{
    contact: Record<string, string>;
    error: string;
  }>;
  uploadBatchId: string;
  uploadDateTime: string;
  created_at?: string;
}

interface UploadResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadResponse: UploadResponse | null;
  timezone?: string;
  /** Name of the file the user uploaded — shown in the post-upload modal. */
  fileName?: string;
}

export const UploadResultModal: FC<UploadResultModalProps> = ({
  isOpen,
  onClose,
  uploadResponse,
  timezone: timezoneProp,
  fileName,
}) => {
  const { organization } = useOrganizationStore();
  const timezone = timezoneProp || organization?.timezone || 'UTC';
  // Function to download errors as CSV
  const downloadErrors = () => {
    if (!uploadResponse?.errors || uploadResponse.errors.length === 0) return;

    // Define the headers in the same order as the template
    const templateHeaders = [
      'crmID',
      'firstName',
      'lastName',
      'phoneNumber',
      'phoneStatus',
      'email',
      'source',
      'tags',
      'street',
      'city',
      'state',
      'zip',
      'birthdate',
    ];

    // Add the Error column to the headers
    const headers = templateHeaders.join(',') + ',Error\n';

    // Convert errors to CSV with values in the correct order
    const rows = uploadResponse.errors
      .map((err) => {
        const values = templateHeaders
          .map((header) => {
            const val = err.contact[header] || '';
            return `"${val}"`;
          })
          .join(',');
        return `${values},"${err.error}"`;
      })
      .join('\n');

    const csv = headers + rows;

    // Create a blob and download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `upload_errors_${uploadResponse.uploadBatchId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <Modal.Header>
        <Typography variant="h4" className="font-bold">
          Upload Complete
        </Typography>
      </Modal.Header>
      <Modal.Body className="pb-10">
        {uploadResponse && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-500">
              <Check className="w-5 h-5" />
              <Typography variant="h5" className="font-medium">
                {uploadResponse.success ? 'Success' : 'Completed with issues'}
              </Typography>
            </div>

            <Typography variant="p" className="text-gray-600 dark:text-gray-400">
              The upload process has completed. Here are the details:
            </Typography>

            {fileName && (
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-3 py-2">
                <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                <span
                  className="text-sm font-medium text-foreground truncate"
                  title={fileName}
                >
                  {fileName}
                </span>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex flex-col gap-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  <span className="font-semibold text-gray-800 dark:text-gray-200">Upload Time:</span>{' '}
                  {formatDate(uploadResponse.created_at || uploadResponse.uploadDateTime, timezone)}
                </p>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  <span className="font-semibold text-gray-800 dark:text-gray-200">Added Contacts:</span>{' '}
                  {uploadResponse.processed}
                </p>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  <span className="font-semibold text-gray-800 dark:text-gray-200">Skipped Contacts:</span>{' '}
                  {uploadResponse.skipped}
                </p>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  <span className="font-semibold text-gray-800 dark:text-gray-200">Total Contacts:</span>{' '}
                  {uploadResponse.total}
                </p>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  <span className="font-semibold text-gray-800 dark:text-gray-200">Errors:</span>{' '}
                  {uploadResponse.errors?.length || 0}
                </p>
              </div>

              {uploadResponse.errors && uploadResponse.errors.length > 0 && (
                <Button
                  variant="outline"
                  className="mt-4 text-red-500 border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                  onClick={downloadErrors}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Errors
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
